import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';

import { type AcquisitionSeriesPoint } from '@/api/routes/partners/partners.acquisition.api';
import { formatAcquisitionCount, hasSeriesActivity } from '@/domain/acquisition/utils';
import { formatReportDay } from '@/domain/results/utils';
import { useTranslate } from '@/state/i18n';
import { controlTokens, radiusTokens } from '@/theme/themePrimitives';

interface Props {
  series: AcquisitionSeriesPoint[];
}

/** Bars of clicks per São Paulo day; the tooltip adds that day's signups and paying people. */
const AcquisitionDailyChart = ({ series }: Props) => {
  const t = useTranslate('domain.acquisition.components.AcquisitionDailyChart');
  const max = Math.max(1, ...series.map((point) => point.clicks));

  return (
    <Paper variant="outlined" sx={sx.paper}>
      <Typography variant="subtitle2" fontWeight={700}>
        {t('title')}
      </Typography>
      {series.length && hasSeriesActivity(series) ? (
        <>
          <Box sx={sx.bars} role="img" aria-label={t('aria')}>
            {series.map((point) => (
              <Tooltip
                key={point.day}
                title={t('tooltip', {
                  day: formatReportDay(point.day),
                  clicks: formatAcquisitionCount(point.clicks),
                  signups: formatAcquisitionCount(point.signups),
                  paying: formatAcquisitionCount(point.paying),
                })}
              >
                <Box sx={sx.slot}>
                  <Box
                    sx={[
                      sx.bar,
                      { height: `${point.clicks ? Math.max(4, (point.clicks / max) * 100) : 0}%` },
                    ]}
                  />
                  {point.signups > 0 ? <Box sx={sx.signupDot} /> : null}
                </Box>
              </Tooltip>
            ))}
          </Box>
          <Stack direction="row" justifyContent="space-between">
            <Typography variant="caption" color="text.secondary">
              {formatReportDay(series[0].day)}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {t('max', { value: formatAcquisitionCount(max) })}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {formatReportDay(series[series.length - 1].day)}
            </Typography>
          </Stack>
          <Stack direction="row" alignItems="center" gap={0.75}>
            <Box sx={sx.dot} />
            <Typography variant="caption" color="text.secondary">
              {t('signupLegend')}
            </Typography>
          </Stack>
        </>
      ) : (
        <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
          {t('empty')}
        </Typography>
      )}
    </Paper>
  );
};

export default AcquisitionDailyChart;

const sx = {
  paper: {
    borderRadius: radiusTokens.xxl,
    p: 2.5,
    display: 'flex',
    flexDirection: 'column',
    gap: 1.5,
  },
  bars: { height: 160, display: 'flex', alignItems: 'stretch', gap: '2px', pt: 1.5 },
  slot: {
    position: 'relative',
    flex: 1,
    minWidth: 2,
    display: 'flex',
    alignItems: 'flex-end',
  },
  bar: { width: '100%', borderRadius: '4px 4px 0 0', bgcolor: controlTokens.cobalt },
  dot: { width: 6, height: 6, borderRadius: '50%', bgcolor: 'success.main', flexShrink: 0 },
  signupDot: {
    position: 'absolute',
    top: 0,
    left: '50%',
    transform: 'translateX(-50%)',
    width: 6,
    height: 6,
    borderRadius: '50%',
    bgcolor: 'success.main',
  },
};
